import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Mail, Copy, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

export default function InvitesPage() {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("planner");
  const [busy, setBusy] = useState(false);

  const enterprise = user?.account_type === "enterprise" && user?.org_id;

  const load = async () => {
    try { const { data } = await api.get("/invites"); setInvites(data); }
    catch(e){ toast.error(errMsg(e)); }
  };
  useEffect(() => { if (enterprise) load(); }, [enterprise]);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/invites", { email, org_role: role });
      setEmail("");
      toast.success("Invitation sent");
      load();
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Revoke invite?")) return;
    try { await api.delete(`/invites/${id}`); setInvites((x)=>x.filter(i=>i.id!==id)); } catch(e){ toast.error(errMsg(e)); }
  };

  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("Invite link copied"); };

  if (!enterprise) {
    return (
      <AppLayout>
        <div className="pc-card p-10 text-center" data-testid="invites-locked">
          <Mail className="mx-auto text-blue-400 mb-3" size={36}/>
          <div className="text-xl font-semibold text-white mb-1" style={{fontFamily:'Outfit'}}>Team invites are an enterprise feature</div>
          <p className="text-slate-400">Create or upgrade to an enterprise account to invite teammates.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white" style={{fontFamily:'Outfit'}}>Team invites</h1>
        <p className="text-slate-400 mt-1">Invite teammates to join your organization.</p>
      </div>

      <form onSubmit={send} className="pc-card p-5 mb-6 flex flex-wrap items-end gap-3" data-testid="invite-form">
        <div className="flex-1 min-w-[220px]">
          <label className="text-xs uppercase tracking-widest text-slate-400">Email</label>
          <input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="teammate@example.com" data-testid="invite-email"
            className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-400">Role</label>
          <select value={role} onChange={(e)=>setRole(e.target.value)} data-testid="invite-role"
            className="px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm">
            <option value="planner">Planner (edit)</option>
            <option value="viewer">Viewer (read-only)</option>
          </select>
        </div>
        <button type="submit" disabled={busy} data-testid="invite-submit"
          className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-60">
          <Send size={14}/> {busy ? "Sending…" : "Send invite"}
        </button>
      </form>

      <div className="pc-card overflow-x-auto" data-testid="invites-table">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-blue-300/80 border-b border-slate-800">
            <tr><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Invite link</th><th className="p-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800/60">
            {invites.length===0
              ? <tr><td colSpan={5} className="p-10 text-center text-slate-400">No invites yet.</td></tr>
              : invites.map(i=> (
                <tr key={i.id} data-testid={`invite-row-${i.id}`}>
                  <td className="p-3 text-white">{i.email}</td>
                  <td className="p-3 text-slate-300 capitalize">{i.org_role}</td>
                  <td className="p-3">
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${i.status==="accepted"?"bg-emerald-500/15 text-emerald-300 border-emerald-500/30":"bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                      {i.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <button onClick={()=>copy(i.accept_url)} data-testid={`copy-invite-${i.id}`} className="text-xs text-blue-300 hover:text-blue-200 inline-flex items-center gap-1 border border-slate-700 rounded px-2 py-1"><Copy size={12}/> Copy link</button>
                  </td>
                  <td className="p-3">
                    <button onClick={()=>remove(i.id)} className="text-slate-500 hover:text-rose-400" data-testid={`delete-invite-${i.id}`}><Trash2 size={14}/></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
